import React, { useState, useEffect } from 'react';
import './App.css';
import Home from './pages/Home';
import Explanation from './pages/Explanation';
import SettingsPage from './pages/SettingsPage';
import LanguageSelector from './components/LanguageSelector';
import { translations } from './data/translations';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [language, setLanguage] = useState('fr');
  const [settings, setSettings] = useState({
    contractionTime: 3,
    relaxationTime: 3,
    iterations: 15,
    contractionColor: '#FF6B6B',
    relaxationColor: '#4ECDC4'
  });
  const [activeBackground, setActiveBackground] = useState(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  useEffect(() => {
    const browserLang = navigator.language.split('-')[0];
    const supportedLangs = Object.keys(translations);
    if (supportedLangs.includes(browserLang)) {
      setLanguage(browserLang);
    }
  }, []);

  useEffect(() => {
    if (currentPage !== 'home') {
      setActiveBackground(null);
    }
  }, [currentPage]);

  useEffect(() => {
    const content = translations[language];
    const pageTitle = content.seoTitle || content.heroTitle || content.appTitle;
    const description = content.seoDescription || content.heroDescription || content.appTitle;
    const keywords = content.seoKeywords;

    document.title = pageTitle;
    document.documentElement.setAttribute('lang', language);

    const setMeta = (selector, value) => {
      const node = document.querySelector(selector);
      if (node && value) {
        node.setAttribute('content', value);
      }
    };

    setMeta('meta[name="description"]', description);
    setMeta('meta[name="keywords"]', keywords);
    setMeta('meta[property="og:title"]', pageTitle);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[name="twitter:title"]', pageTitle);
    setMeta('meta[name="twitter:description"]', description);
  }, [language]);

  const togglePage = (targetPage) => {
    setCurrentPage((prev) => (prev === targetPage ? 'home' : targetPage));
  };

  const goHome = () => {
    setCurrentPage('home');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'explanation':
        return <Explanation language={language} onNavigateHome={goHome} />;
      case 'settings':
        return (
          <SettingsPage
            settings={settings}
            setSettings={setSettings}
            language={language}
            onNavigateHome={goHome}
          />
        );
      default:
        return (
          <Home
            settings={settings}
            language={language}
            onBackgroundChange={setActiveBackground}
            onTimerRunChange={setIsTimerRunning}
          />
        );
    }
  };

  return (
    <div
      className={`App ${activeBackground ? 'App--active' : ''}`}
      style={activeBackground ? { background: activeBackground } : undefined}
    >
      <div className={`top-controls ${isTimerRunning ? 'controls--dimmed' : ''}`}>
        <LanguageSelector language={language} setLanguage={setLanguage} />
        <button
          type="button"
          className={`icon-button ${currentPage === 'settings' ? 'active' : ''}`}
          onClick={() => togglePage('settings')}
          aria-label={translations[language].settingsNavLabel}
          title={translations[language].settingsNavLabel}
        >
          &#9881;
        </button>
      </div>

      <main className="main-content">
        {renderPage()}
      </main>

      <button
        type="button"
        className={`help-button ${currentPage === 'explanation' ? 'active' : ''} ${isTimerRunning ? 'controls--dimmed' : ''}`}
        onClick={() => togglePage('explanation')}
        aria-label={translations[language].explanationNavLabel}
        title={translations[language].explanationNavLabel}
      >
        ?
      </button>
    </div>
  );
}

export default App;
